import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';
import { ReconnectSquare } from './ReconnectSquare';
import { AgreementPanel, hasAgreementMetadata } from './AgreementPanel';
import { AllowanceNotice } from './AllowanceNotice';
import { ASSISTANT_NAME } from '../brand.js';

const ENDPOINT = '/v1/owner/billing';
const buttonStyle = { padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-subtle)', background: 'white', cursor: 'pointer', minHeight: '44px' };
const statuses = { active: 'Active', past_due: 'Payment overdue', unpaid: 'Payment required', canceled: 'Canceled', incomplete: 'Payment incomplete', incomplete_expired: 'Checkout expired', trialing: 'Trial', paused: 'Paused', none: 'Not subscribed', not_subscribed: 'Not subscribed', review_required: 'Billing review required', needs_payment: 'Payment required' };

function dateLabel(value) {
  if (!value) return 'Not available';
  const date = new Date(typeof value === 'number' ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function BillingPanel({ needsPayment = false, autoReconcile = false, onUpdated, guided = false, onConnectionIssue }) {
  const headingId = useId();
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState('load');
  const [error, setError] = useState('');
  const [salesPaused, setSalesPaused] = useState(false);
  const [notice, setNotice] = useState('');
  const running = useRef(false);
  const mounted = useRef(false);
  const timer = useRef(null);
  const finishWait = useRef(null);
  const callback = useRef(onUpdated);
  callback.current = onUpdated;

  useEffect(() => {
    mounted.current = true;
    update(autoReconcile);
    return () => { mounted.current = false; clearTimeout(timer.current); finishWait.current?.(); };
  }, []);

  async function update(reconcile = false) {
    if (running.current) return;
    running.current = true;
    setBusy(reconcile ? 'refresh' : 'load');
    setError('');
    setSalesPaused(false);
    setNotice(reconcile ? 'Checking your subscription with Stripe…' : new URLSearchParams(window.location.search).get('billing') === 'canceled' ? 'Checkout was closed. Your current subscription status is shown below.' : '');
    let initial;
    try {
      if (reconcile) await apiFetch(`${ENDPOINT}/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      // A return URL is not payment evidence. Only the server's projection is displayed.
      for (let attempt = 0; attempt < (reconcile ? 10 : 1); attempt += 1) {
        const data = await apiFetch(ENDPOINT);
        if (!mounted.current) return;
        if (!data || typeof data.status !== 'string') throw new Error('Billing information is temporarily unavailable.');
        setBilling(data);
        if (attempt === 0) initial = data.reconciled_at;
        if (!reconcile || data.funding_source === 'internal' || (attempt > 0 && data.reconciled_at && data.reconciled_at !== initial)) {
          if (reconcile) setNotice('Billing information updated.');
          if (reconcile) callback.current?.();
          return;
        }
        if (attempt < 9) await new Promise(resolve => { finishWait.current = resolve; timer.current = setTimeout(resolve, 3000); });
        if (!mounted.current) return;
      }
      setNotice('Still waiting for the latest billing update. You can check again shortly.');
      callback.current?.();
    } catch (err) {
      if (mounted.current) setError(err.message || 'Could not load billing. Please try again.');
    } finally {
      running.current = false;
      if (mounted.current) setBusy('');
    }
  }

  async function redirect(action) {
    if (running.current || (action === 'checkout' && (!hasAgreementMetadata(billing?.agreement) || billing.agreement.accepted !== true))) return;
    running.current = true;
    setBusy(action);
    setError('');
    try {
      const result = await apiFetch(`${ENDPOINT}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const url = new URL(result?.url);
      const host = action === 'checkout' ? 'checkout.stripe.com' : 'billing.stripe.com';
      if (url.protocol !== 'https:' || url.hostname !== host || url.port || url.username || url.password) throw new Error('The billing link could not be verified. Please try again.');
      if (mounted.current) window.location.assign(url.href);
    } catch (err) {
      if (mounted.current) { setError(err.message || 'Could not open Stripe. Please try again.'); setSalesPaused(err.code === 'subscriptions_paused'); }
      running.current = false;
      if (mounted.current) setBusy('');
      if (err.code === 'square_not_ready') onConnectionIssue?.();
      if (['agreement_required', 'agreement_changed'].includes(err.code)) {
        setBilling(current => ({ ...current, agreement: null }));
        await update();
        callback.current?.();
        if (mounted.current) setError('Review and accept the current signup agreement before subscribing.');
      }
    }
  }

  const money = (value, divisor) => {
    if (typeof value !== 'number' || !Number.isFinite(value) || typeof billing?.currency !== 'string') return 'Not available';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: billing.currency.toUpperCase() }).format(value / divisor); }
    catch { return 'Not available'; }
  };
  const internal = billing?.funding_source === 'internal';
  const canCheckout = billing && !internal && ['not_subscribed', 'none', 'needs_payment', 'canceled', 'incomplete_expired'].includes(billing.status);
  const hasSupplement = Number.isSafeInteger(billing?.supplemental_micro) && billing.supplemental_micro > 0;
  const offerAllowance = billing?.plan_allowance_micro ?? billing?.allowance_micro;

  if (guided) return <>
    <p class="setup-eyebrow">YOUR ONLINE FRONT DESK</p><h2>{internal ? 'Your setup is funded.' : 'A place for every booking.'}</h2>
    <p>{internal ? `Your internal account is ready for ${ASSISTANT_NAME} to learn your salon.` : `Your salon’s booking page, a website widget, and ${ASSISTANT_NAME} to help clients find their next appointment.`}</p>
    {billing && !internal && <><p class="setup-offer-price">{money(billing.monthly_cents, 100)} <span>/ salon / month</span></p><ul class="setup-inclusions"><li>Your own booking page, ready to share</li><li>{money(offerAllowance, 1000000)} in AI usage each billing period, shared across setup and conversations</li><li>No automatic overage charges; unused allowance does not roll over</li><li>AI assistance may pause when your included allowance and any backup allowance are used up.</li></ul></>}
    {busy && <p role="status">{busy === 'checkout' ? 'Opening secure checkout…' : busy === 'refresh' ? 'Checking your payment…' : 'Loading your subscription…'}</p>}
    {!busy && notice && <p role="status">{notice}</p>}
    {error && <p role={salesPaused ? 'status' : 'alert'}>{error}</p>}
    {!salesPaused && canCheckout && <button class="setup-primary" disabled={!!busy || billing.automatic_overages !== false || !hasAgreementMetadata(billing.agreement) || billing.agreement.accepted !== true} onClick={() => redirect('checkout')}>{busy === 'checkout' ? 'Opening Stripe…' : 'Continue to secure checkout'}</button>}
    {billing && !canCheckout && !salesPaused && <button class="setup-primary" disabled={!!busy} onClick={() => billing.has_billing_account && !internal ? redirect('portal') : update(true)}>{billing.has_billing_account && !internal ? 'Review your subscription' : 'Continue setup'}</button>}
    {(error || salesPaused || (!busy && notice)) && <button class="setup-text-button" disabled={!!busy} onClick={() => update(true)}>Check status again</button>}
    {!billing && !busy && <button class="setup-primary" onClick={() => update()}>Try again</button>}
    {salesPaused && <a class="setup-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }} href="mailto:support@gojonathan.ai">Contact {ASSISTANT_NAME}</a>}
    {billing && !internal && !salesPaused && <p style={{ fontSize: '12px', textAlign: 'center', marginTop: '18px' }}>Payments are handled securely by Stripe. Your setup starts after payment is confirmed.</p>}
  </>;

  return (
    <section aria-labelledby={headingId} style={{ padding: '1.5rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.6 }}>
      <h1 id={headingId} style={{ fontSize: '1.5rem', margin: '0 0 0.5rem' }}>{internal ? 'Internal account & AI allowance' : needsPayment ? 'Start your salon subscription' : 'Billing & AI allowance'}</h1>
      {needsPayment && !internal && <p>Choose your subscription before {ASSISTANT_NAME} learns your menu. Your salon’s booking page and widget are included.</p>}
      {billing && <>
        <AllowanceNotice billing={billing} />
        {internal ? <>
          <p style={{ fontSize: '1.375rem', fontWeight: 600 }}>Internal account — no subscription charge</p>
          <p>{ASSISTANT_NAME} funds this account for internal use. AI costs are still measured against a finite base allowance of {money(billing.allowance_micro, 1000000)} through {dateLabel(billing.period_end)}. Contact support to renew or adjust the allowance.</p>
        </> : <>
        <p style={{ fontSize: '1.375rem', fontWeight: 600 }}>{money(billing.monthly_cents, 100)} <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>per salon / month</span></p>
        <p>{canCheckout ? 'Includes' : 'Base allowance for this period:'} {money(canCheckout ? offerAllowance : billing.allowance_micro, 1000000)} in AI usage{canCheckout ? ' each billing period' : ''}, shared across guest conversations, owner chat and setup. Unused allowance does not roll over.</p>
        </>}
        <p>{billing.automatic_overages === false ? 'No automatic overage charges. AI may pause if the remaining allowance cannot cover a request; you can still manage your account.' : 'Overage policy unavailable. Contact support before subscribing.'}</p>
        {hasSupplement && <p>{ASSISTANT_NAME} has added {money(billing.supplemental_micro, 1000000)} of additional AI allowance at no extra charge for the period ending {dateLabel(billing.period_end)}. Unused additional allowance expires with this period.</p>}
        <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', margin: '1.5rem 0' }}>
          <dt>Account status</dt><dd style={{ margin: 0 }}>{internal ? 'Internal account' : statuses[billing.status] || 'Status unavailable'}</dd>
          {hasSupplement && <>
            <dt>Base allowance this period</dt><dd style={{ margin: 0 }}>{money(billing.base_allowance_micro, 1000000)}</dd>
            <dt>Additional allowance from {ASSISTANT_NAME}</dt><dd style={{ margin: 0 }}>{money(billing.supplemental_micro, 1000000)}</dd>
            <dt>Total allowance this period</dt><dd style={{ margin: 0 }}>{money(billing.total_allowance_micro, 1000000)}</dd>
          </>}
          <dt>AI allowance remaining</dt><dd style={{ margin: 0 }}>{money(billing.remaining_micro, 1000000)}</dd>
          <dt>AI usage this period</dt><dd style={{ margin: 0 }}>{money(billing.spent_micro, 1000000)}</dd>
          <dt>Reserved for pending or unresolved requests</dt><dd style={{ margin: 0 }}>{money(billing.reserved_micro, 1000000)}</dd>
          <dt>{billing.cancel_at_period_end ? 'Cancellation scheduled for' : 'Current period ends'}</dt><dd style={{ margin: 0 }}>{dateLabel(billing.period_end)}</dd>
        </dl>
        {billing.cancel_at_period_end && <p>Your subscription is set to end on {dateLabel(billing.period_end)}. Manage billing to review your cancellation.</p>}
        {['FAILED', 'REVIEW_REQUIRED'].includes(billing.setup_status) && <p role="status">Setup needs attention. Contact support; your billing controls remain available.</p>}
        {!billing.funded_period && <p>There is no funded AI allowance for the current period.</p>}
      </>}
      {billing && <AgreementPanel agreement={billing.agreement} internal={internal} disabled={!!busy} onReload={async () => { await update(); callback.current?.(); }} />}
      <div role="status" aria-live="polite">{busy === 'load' ? 'Loading billing…' : notice}</div>
      {error && <p role="alert" style={{ color: 'var(--coral)' }}>{error}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
        {canCheckout && <button type="button" disabled={!!busy || billing.automatic_overages !== false || !hasAgreementMetadata(billing.agreement) || billing.agreement.accepted !== true} style={{ ...buttonStyle, background: 'var(--sage)', color: 'white' }} onClick={() => redirect('checkout')}>{busy === 'checkout' ? 'Opening Stripe…' : 'Subscribe with Stripe'}</button>}
        {billing?.has_billing_account && <button type="button" disabled={!!busy} style={buttonStyle} onClick={() => redirect('portal')}>{busy === 'portal' ? 'Opening Stripe…' : 'Manage billing in Stripe'}</button>}
        <button type="button" disabled={!!busy} style={buttonStyle} onClick={() => update(true)}>{busy === 'refresh' ? 'Checking…' : 'Refresh billing status'}</button>
      </div>
      {needsPayment && <ReconnectSquare />}
    </section>
  );
}

export function BillingDialog({ onClose, onUpdated, autoReconcile = false }) {
  const dialog = useRef(null);
  useEffect(() => {
    dialog.current?.showModal();
    return () => dialog.current?.close();
  }, []);
  return <dialog ref={dialog} onCancel={onClose} onClose={onClose} aria-label="Salon billing" style={{ width: 'min(600px, calc(100vw - 2rem))', maxHeight: 'calc(100dvh - 2rem)', overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: 0, background: 'var(--cream)', color: 'var(--black)' }}>
    <div style={{ padding: '1rem 1rem 0', textAlign: 'right' }}><button type="button" style={buttonStyle} onClick={onClose}>Close billing</button></div>
    <BillingPanel autoReconcile={autoReconcile} onUpdated={onUpdated} />
  </dialog>;
}
